import * as Sentry from '@sentry/react';
import { AUTH_JS_NOISE_ERROR_NAMES, isTransientNetworkError } from './networkErrors';

/**
 * Saudi mobile numbers in the formats events actually carry — mirror of
 * `apps/api/src/config/sentry.config.ts` (duplicated, not shared via
 * `@dallali/shared`, because apps/api must never runtime-import that package).
 * Prefixed forms (`+966`/`966`/`00966`/`05…`) tolerate space/dash separators;
 * the bare 9-digit `5XXXXXXXX` form must be one unbroken run. The
 * `(?<!\d)`/`(?!\d)` guards stop partial matches inside longer numeric runs
 * (invoice ids, timestamps, UUID node segments).
 *
 * Stateful `/g` flag: use ONLY via `String.replace()` (which resets
 * `lastIndex`) — never call `.exec()`/`.test()` on this shared const.
 */
const SAUDI_PHONE_RE =
  /(?<!\d)(?:(?:\+|00)?966[\s-]?5(?:[\s-]?\d){8}|05(?:[\s-]?\d){8}|5\d{8})(?!\d)/g;

/**
 * A stack frame whose function name is a native DOM method (e.g.
 * `HTMLDivElement.appendChild`) — real natives never carry a source location,
 * so such a frame resolving to a non-http(s) script can only be a
 * monkey-patched native installed by injected third-party code.
 */
const NATIVE_DOM_METHOD_RE =
  /^(?:HTML\w*Element|SVG\w*Element|Element|Node|Document|EventTarget|Window)\.\w+$/;

/** Masking convention: keep ONLY the last 4 digits — `***4567`. */
const maskPhone = (match: string): string =>
  `***${match.replace(/\D/g, '').slice(-4)}`;

/**
 * Replace every Saudi phone in `text` with its `***<last4>` mask.
 * Exported for unit tests.
 */
export function scrubPhones(text: string): string {
  return text.replace(SAUDI_PHONE_RE, maskPhone);
}

/**
 * Saudi national/iqama IDs — mirror of
 * `apps/api/src/common/pii/mask.ts`'s `getSaudiNationalIdRe` (duplicated for
 * the same runtime-import-boundary reason as `SAUDI_PHONE_RE` above): an
 * unbroken run of exactly 10 digits starting with `1` (citizen) or `2`
 * (resident/iqama), guarded so it can't land inside a longer digit run.
 *
 * KNOWN TRADEOFF: a 10-digit second-epoch Unix timestamp (any time from
 * 2001-09-09 to 2286-11-20) also starts with `1` and is digit-for-digit
 * indistinguishable from a real national/iqama ID, so it will
 * false-positive-match and get masked. Accepted: over-masking an occasional
 * raw timestamp costs far less than under-masking a real ID.
 *
 * Stateful `/g` flag: use ONLY via `String.replace()` (which resets
 * `lastIndex`) — never call `.exec()`/`.test()` on this shared const.
 */
const SAUDI_NATIONAL_ID_RE = /(?<!\d)[12]\d{9}(?!\d)/g;

/**
 * Replace every Saudi national/iqama ID in `text` with its `***<last4>`
 * mask. Exported for unit tests.
 */
export function scrubNationalIds(text: string): string {
  return text.replace(SAUDI_NATIONAL_ID_RE, (match) => `***${match.slice(-4)}`);
}

/**
 * Saudi IBANs — mirror of `apps/api/src/common/pii/mask.ts`'s
 * `getSaudiIbanRe`: `SA` followed by 22 digits, optionally space-separated.
 *
 * Stateful `/g` flag: use ONLY via `String.replace()` (which resets
 * `lastIndex`) — never call `.exec()`/`.test()` on this shared const.
 */
const SAUDI_IBAN_RE = /\bSA\d{2}(?:[ ]?\d){20}\b/gi;

/** Masking convention: keep ONLY the last 4 digits — `***4567`. */
const maskIban = (match: string): string =>
  `***${match.replace(/\s/g, '').slice(-4)}`;

/**
 * Replace every Saudi IBAN in `text` with its `***<last4>` mask.
 * Exported for unit tests.
 */
export function scrubIbans(text: string): string {
  return text.replace(SAUDI_IBAN_RE, maskIban);
}

/**
 * Apply every free-text PII scrubber (phones, national IDs, IBANs) in one
 * pass — mirrors `scrubPII` in `apps/api/src/config/sentry.config.ts`.
 */
function scrubPII(text: string): string {
  return scrubIbans(scrubNationalIds(scrubPhones(text)));
}

/**
 * Unhandled promise rejection handler — the single capture path for
 * `unhandledrejection` (M8 item 3).
 *
 * The SDK's default GlobalHandlers integration would also capture these when
 * Sentry is initialized, so its `onunhandledrejection` half is disabled in
 * `initializeSentry` below and THIS listener owns the event in both states
 * (no double-capture):
 *  - Sentry enabled  → captureException tagged `mechanism: unhandledrejection`;
 *  - Sentry disabled (dev / no DSN) → console.error so rejections are at
 *    least surfaced instead of vanishing silently.
 *
 * Exported for unit tests.
 */
export function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason: unknown = event.reason;

  if (Sentry.getClient()) {
    const error =
      reason instanceof Error
        ? reason
        : new Error(`Unhandled promise rejection: ${serializeError(reason)}`);
    try {
      Sentry.captureException(error, {
        tags: { mechanism: 'unhandledrejection' },
      });
    } catch (sentryError) {
      // Circuit-breaker: a throwing SDK must not escape a global handler
      // (window.onerror would re-capture it — needless error loop risk).
      console.error(
        '[sentry] captureException threw in unhandledrejection handler:',
        sentryError,
        reason,
      );
    }
  } else {
    console.error('[sentry-disabled] Unhandled promise rejection:', reason);
  }
}

/**
 * Initialize Sentry for error tracking, performance monitoring, and session replay
 * Must be called FIRST in main.tsx before React render
 *
 * Environments: deploy.yml builds with `vite build --mode <branch>`, so MODE
 * is 'staging' on staging builds and 'production' on prod — the gate below
 * only skips local dev. Whether staging actually reports hinges on the
 * VITE_SENTRY_DSN secret being provisioned for the staging GitHub environment.
 */
export function initializeSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE;
  const release = import.meta.env.VITE_SENTRY_RELEASE || 'development';

  if (environment === 'development' || !dsn) {
    // Sentry off — still surface unhandled rejections on the console.
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // Performance Monitoring & Error Capture
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text, inputs, and media by default
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      // Capture every console.error() as a Sentry event
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      // Capture failed HTTP responses (4xx/5xx) from fetch/XHR
      Sentry.httpClientIntegration(),
      // Keep the default onerror handler but hand `unhandledrejection` to OUR
      // tagged listener (registered below) — a same-name user instance
      // replaces the SDK default, so this does not double-capture.
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: false,
      }),
    ],

    // Filter non-actionable errors
    denyUrls: [
      // SW registration failures (browser/network conditions, not app bugs)
      /registerSW\.js$/,
      // Browser-extension content scripts — third-party code we can't fix.
      // Complementary to the injected-script filter in beforeSend below:
      // denyUrls catches errors whose top frame IS the extension script;
      // beforeSend catches extension-patched natives surfacing under an
      // app-looking stack (e.g. inside React's commit).
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari(-web)?-extension:\/\//i,
    ],

    // Required for httpClientIntegration to attach request/response info
    sendDefaultPii: true,

    // Performance sample rate: 10% of transactions
    tracesSampleRate: 0.1,

    // Session Replay sample rates
    replaysSessionSampleRate: 0.1, // 10% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of error sessions

    // PII Scrubbing & Error Filtering
    beforeSend: (event, hint) => {
      // CIRCUIT-BREAKER (API parity): if filtering/scrubbing throws, DROP the
      // event — never send it unscrubbed. Fail CLOSED for PII.
      try {
      // Filter transient network errors (already handled by React Query retry logic)
      // Check both hint.originalException and event.exception for httpClientIntegration errors
      if (isTransientNetworkError(hint?.originalException)) {
        return null;
      }

      // httpClientIntegration may store errors in event.exception instead of hint
      if (event.exception?.values?.[0]?.value) {
        if (isTransientNetworkError(event.exception.values[0].value)) {
          return null;
        }
      }

      // Also check event message for abort errors
      if (event.message && isTransientNetworkError(event.message)) {
        return null;
      }

      // Filter errors with no meaningful message (noise from console.error with empty objects)
      // This happens when console.error() is called with error objects that don't serialize properly
      const hasNoMessage = !event.message || event.message === 'No error message' || event.message.trim() === '';
      const hasNoException = !event.exception?.values?.[0]?.value;
      if (hasNoMessage && hasNoException) {
        return null; // Drop event - no actionable information
      }

      // console.error(<object>) via captureConsoleIntegration stringifies to a
      // bare "[object Object]" when the object has no useful toString and no
      // string prefix accompanies it — a non-empty message that sails past the
      // hasNoMessage drop yet carries zero signal (#3770; same rationale as
      // the captureAnonRead noise drop, #2211). Only the bare form is dropped:
      // a prefixed "<context>: [object Object]" still names its call site and
      // stays reportable.
      if (event.message?.trim() === '[object Object]' && hasNoException) {
        return null; // Drop event - unserialized console.error payload, no actionable information
      }

      // Filter expected demo account errors (not actual bugs)
      const error = hint?.originalException;
      if (
        error instanceof Error &&
        error.message === 'Demo account - write actions disabled'
      ) {
        return null; // Drop event - this is expected behavior, not a production error
      }

      // Filter expected auth errors (expired sessions, invalid tokens, revoked credentials)
      // These arrive via multiple paths:
      // 1. Error instances (hint.originalException)
      // 2. Exception values (event.exception.values[0].value)
      // 3. Exception types (event.exception.values[0].type) — CRITICAL for Supabase AuthApiError
      // 4. Console.error messages (event.message)
      // 5. HTTP response data from httpClientIntegration (event.contexts.response)
      const authNoisePatterns = [
        'Invalid Refresh Token',
        'Refresh Token Not Found',
        'Invalid authentication credentials',
        'invalid_grant', // OAuth2 error code from Supabase
        'invalid grant',
        // NestJS guard rejects an expired Supabase JWT — handled by the
        // fallback pattern and isNestAuthError in QueryCache.onError.
        // Defense-in-depth: also drop here in case the error reaches beforeSend
        // via httpClientIntegration or an unhandled rejection (Sentry #3076).
        'Invalid or expired token',
        // AuthSessionMissingError sometimes arrives wrapped in a plain Error,
        // where only its message survives — match the message too.
        'Auth session missing',
        // The auth-js typed error classes classified as noise — matched via
        // the exceptionType check below, because their messages are dynamic
        // or empty ("{}"; Sentry #906, #3575). The classification of ALL
        // auth-js error classes lives on AUTH_JS_NOISE_ERROR_NAMES in
        // networkErrors.ts — extend it there, never inline here.
        ...AUTH_JS_NOISE_ERROR_NAMES,
      ];
      const errorMsg = error instanceof Error ? error.message : '';
      const exceptionMsg = event.exception?.values?.[0]?.value ?? '';
      const exceptionType = event.exception?.values?.[0]?.type ?? '';
      const eventMsg = event.message ?? '';

      // Check httpClientIntegration response data (where Supabase auth errors often appear)
      const responseCtx = (event.contexts?.response ?? {}) as Record<string, unknown>;
      const responseData = responseCtx.data;
      const responseDataStr = responseData
        ? (typeof responseData === 'string' ? responseData : JSON.stringify(responseData))
        : '';

      if (
        authNoisePatterns.some(
          (p) =>
            errorMsg.includes(p) ||
            exceptionMsg.includes(p) ||
            // Exact equality, NOT substring: the exhaustiveness gate in
            // networkErrors.test.ts only enumerates publicly exported auth-js
            // classes, so a non-exported subclass whose name merely CONTAINS a
            // noise-class name (e.g. `AuthApiErrorInternal`) must not be
            // silently dropped by a substring match (#3596).
            exceptionType === p ||
            eventMsg.includes(p) ||
            responseDataStr.includes(p),
        )
      ) {
        return null; // Drop event - handled gracefully by auth state listener / UI
      }

      // Third-party injected-script interference (#3610/#3611): browser
      // extensions monkey-patch native DOM methods (appendChild et al.) and
      // JSON.stringify the React-managed nodes they intercept. The circular
      // `__reactFiber$…` reference makes that stringify throw inside React's
      // commit, so the event looks like an app crash while no app code is on
      // the stack — the patched "native" frame reports from the injected
      // script (`<anonymous>` or an extension:// URL), never from our bundle.
      // Drop ONLY when both hold:
      //   1. the TypeError names a React fiber (a React-managed DOM element
      //      was serialized), and
      //   2. a frame claiming to be a native DOM method resolves to a
      //      non-http(s) script — impossible for real natives (no location)
      //      and for our code (bundles load over https).
      // App code stringifying a DOM node keeps its https frames and still
      // reports; a missing stack keeps the event too (fail open to reporting).
      if (
        exceptionType === 'TypeError' &&
        exceptionMsg.includes('Converting circular structure to JSON') &&
        exceptionMsg.includes('__reactFiber$')
      ) {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
        const hasPatchedNativeFrame = frames.some(
          (frame) =>
            NATIVE_DOM_METHOD_RE.test(frame.function ?? '') &&
            !/^https?:\/\//.test(frame.filename ?? ''),
        );
        if (hasPatchedNativeFrame) {
          return null; // Injected-script breakage, not an app bug
        }
      }

      // Also filter auth errors by checking if they're from Supabase auth endpoints
      const requestUrl = event.request?.url || String(responseCtx.url ?? '');
      if (requestUrl.includes('/auth/v1/token') || requestUrl.includes('/auth/v1/user')) {
        // Any 401/403 from Supabase auth endpoints are expected auth errors
        const statusCode = responseCtx.status_code;
        if (statusCode === 401 || statusCode === 403) {
          return null; // Drop event - expected auth failure
        }
      }

      // Drop httpClientIntegration 5xx noise from the NestJS API.
      // The repository layer's try/catch handles these via Supabase fallback, and each
      // fallback is already reported at the right granularity by reportNestFallback()
      // as a "nest-fallback" captureMessage event. The duplicate httpClientIntegration
      // exception ("HTTP Client Error with status code: 500") just fires the alert twice
      // and buries the tagged fallback signal.
      const nestApiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');
      let isNestApiRequest = false;
      if (nestApiBase) {
        // Precise origin match (not startsWith — avoids matching a longer host that
        // merely shares the prefix). A malformed URL falls through to keep the event.
        try {
          isNestApiRequest = new URL(requestUrl).origin === new URL(nestApiBase).origin;
        } catch {
          isNestApiRequest = false;
        }
      } else {
        // VITE_API_URL unset (shouldn't happen in a deployed env) → known Nest hosts.
        isNestApiRequest =
          requestUrl.includes('p1.dallali.com') ||
          requestUrl.includes('dev-api.dallali.com') ||
          requestUrl.includes('staging-api.dallali.com');
      }
      if (isNestApiRequest) {
        const nestStatus = responseCtx.status_code as number | undefined;
        // 5xx: already reported via the structured nest-fallback captureMessage.
        if (nestStatus !== undefined && nestStatus >= 500) {
          return null; // Reported via nest-fallback; drop httpClientIntegration duplicate
        }
        // 401: GoTrue rejected an expired Supabase JWT — self-healing auth error.
        // The fallback pattern (reportNestFallback + Supabase retry) handles it;
        // Nest-only repos (brokerageContracts, teams, leads) propagate it to
        // QueryCache.onError which is already gated by isNestAuthError (Sentry #3076).
        // Deliberately drops ALL Nest 401s: httpClientIntegration events carry
        // no response body, so the drop can't be narrowed to the expired-token
        // message. A systemic auth misconfiguration still surfaces as visible
        // breakage in every session, not only via Sentry.
        if (nestStatus === 401) {
          return null;
        }
      }

      // Scrub sensitive headers (API parity) — sendDefaultPii +
      // httpClientIntegration can capture JWTs / API keys / cookies.
      if (event.request?.headers) {
        for (const header of ['authorization', 'x-api-key', 'cookie']) {
          // Browser-side header names are not case-normalized like Node's.
          for (const key of Object.keys(event.request.headers)) {
            if (key.toLowerCase() === header) {
              delete event.request.headers[key];
            }
          }
        }
      }

      // Scrub phone numbers from ALL string fields of event.user (API
      // parity), then the long-standing unconditional username mask.
      if (event.user) {
        const user = event.user as Record<string, unknown>;
        for (const [key, value] of Object.entries(user)) {
          if (typeof value === 'string') {
            user[key] = scrubPII(value);
          }
        }
        const username = event.user.username;
        if (
          typeof username === 'string' &&
          username.length > 4 &&
          !username.startsWith('***')
        ) {
          event.user.username = `***${username.slice(-4)}`;
        }
      }

      // Scrub phone from request data — with sendDefaultPii +
      // httpClientIntegration, captured request bodies (e.g. Supabase auth
      // calls) can carry the phone the user logged in with.
      if (event.request?.data) {
        try {
          const raw =
            typeof event.request.data === 'string'
              ? event.request.data
              : JSON.stringify(event.request.data);
          const scrubbed = scrubPII(raw);
          try {
            event.request.data = JSON.parse(scrubbed);
          } catch {
            // Not valid JSON (was a plain string) — keep the scrubbed string
            event.request.data = scrubbed;
          }
        } catch {
          // Unserializable (circular) — drop the payload rather than leak it
          event.request.data = { scrubbed: true };
        }
      }

      // Scrub phone from breadcrumbs (messages + data)
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (typeof breadcrumb.message === 'string') {
            breadcrumb.message = scrubPII(breadcrumb.message);
          }
          if (breadcrumb.data) {
            try {
              breadcrumb.data = JSON.parse(scrubPII(JSON.stringify(breadcrumb.data)));
            } catch {
              // JSON.stringify can throw on circular references (stack overflow)
              breadcrumb.data = { scrubbed: true };
            }
          }
          return breadcrumb;
        });
      }

      // Scrub phone from exception messages and the event message — non-Error
      // rejection reasons (e.g. Supabase errors) are serialized into
      // exception.value by handleUnhandledRejection and may carry a phone.
      for (const exceptionValue of event.exception?.values ?? []) {
        if (typeof exceptionValue.value === 'string') {
          exceptionValue.value = scrubPII(exceptionValue.value);
        }
      }
      if (typeof event.message === 'string') {
        event.message = scrubPII(event.message);
      }

      return event;
      } catch (err) {
        console.error(
          '[sentry] beforeSend filter/scrub threw — dropping event (fail closed for PII)',
          err,
        );
        return null;
      }
    },
  });

  // Single owner of `unhandledrejection` (the SDK's default handler for it is
  // disabled above). Registered AFTER init so Sentry.getClient() is set.
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}

/**
 * Set user context in Sentry
 * Call after successful authentication
 */
export function setSentryUser(user: {
  id: string;
  phone?: string;
  company_id?: string;
}) {
  // Extract last 4 digits of phone
  const phoneLastFour = user.phone ? user.phone.slice(-4) : undefined;

  Sentry.setUser({
    id: user.id,
    username: phoneLastFour ? `***${phoneLastFour}` : undefined,
    company_id: user.company_id,
  });
}

/**
 * Clear user context from Sentry
 * Call on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Why a Nest session was lost. The `string & Record<never, never>` arm preserves
 * the literal autocomplete hints WITHOUT widening to a bare `string` (a plain
 * `'network' | '5xx' | string` union collapses to `string` in TS and buys nothing),
 * while still allowing a concrete 4xx status string like `'401'`.
 */
export type SessionLostReason =
  | 'network'
  | '5xx'
  | 'unknown'
  // Dead session detected via the query-error path (sessionHeal.ts): the heal
  // finished an involuntary sign-out the auth layer never completed.
  | 'dead_session_query_storm'
  | (string & Record<never, never>);

/** Dedup window for {@link captureSessionLost} — collapse multi-tab/concurrent bursts. */
const SESSION_LOST_DEDUP_WINDOW_MS = 5000;
const SESSION_LOST_DEDUP_KEY = 'dallali-session-lost-last';

/**
 * Report a Nest session being lost (forced sign-out after a refresh failed).
 *
 * Backend refresh failures are 401s, which are NOT captured server-side, so this
 * is the only signal that the logout-on-deploy bug recurred. Tagged with the
 * release + reason so it can be correlated against deploy timestamps. The message
 * deliberately avoids the auth-noise patterns filtered in `beforeSend` above so it
 * is NOT dropped.
 *
 * SEVERITY SPLIT (2026-07-10 tag breakdown):
 * - `error` ONLY when `outcome === 'reuse_detected'` — the rotated refresh token
 *   was reused by another session (potential token theft / replay attack). Keep
 *   this loud: 50/121 events at the time of the breakdown (DALLALI-FRONTEND-AQ).
 * - `warning` for every other outcome (`invalid_or_expired`, `network`,
 *   `no_session`, or undefined) — these are benign refresh-lifecycle signals
 *   (71/121 events) that don't warrant an alert.
 *
 * Deduplicated within a short window so concurrent/multi-tab refresh failures
 * don't spam one logout.
 */
export function captureSessionLost(params: {
  reason: SessionLostReason;
  /** Where in the auth flow it happened (e.g. 'recover' | 'scheduled-refresh' | 'focus-refresh'). */
  where: string;
  status?: number;
  /** Backend rotate outcome behind a 401: 'reuse_detected' (theft-revoke) vs
   *  'invalid_or_expired' (benign). The signal that tells fix-working-as-designed
   *  apart from fix-over-revoking. Undefined for network/5xx. */
  outcome?: string;
  route: string;
}) {
  // Collapse bursts (e.g. several tabs refreshing at once) into one event.
  try {
    const last = Number(sessionStorage.getItem(SESSION_LOST_DEDUP_KEY) ?? 0);
    const now = Date.now();
    if (now - last < SESSION_LOST_DEDUP_WINDOW_MS) return;
    sessionStorage.setItem(SESSION_LOST_DEDUP_KEY, String(now));
  } catch {
    // sessionStorage unavailable (SSR / privacy mode) — fall through and report.
  }

  Sentry.captureMessage('[auth] nest session lost — forced signout', {
    level: params.outcome === 'reuse_detected' ? 'error' : 'warning',
    tags: {
      type: 'auth_session_lost',
      reason: params.reason,
      where: params.where,
      outcome: params.outcome ?? 'none',
    },
    extra: {
      route: params.route,
      status: params.status,
      release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
    },
  });
}

/** Dedup window for {@link captureNoAccessRedirect} — its own constant so it
 *  isn't silently coupled to the session-lost window. */
const NO_ACCESS_DEDUP_WINDOW_MS = 5_000;
const NO_ACCESS_DEDUP_KEY = 'dallali-no-access-redirect-last';

/**
 * Report that a user actually landed on /no-access (after the route-guard grace
 * window + self-heal had their chance). The discriminators:
 *  - `authenticated=true` + the latest `release` → a logged-in user is still
 *    being stranded here AFTER the focus-refresh grace fix, i.e. the grace
 *    didn't fully cover the race (or it's a genuinely roleless account).
 *  - `authenticated=false` → an unauthenticated user reached /no-access instead
 *    of /auth — a routing bug.
 */
export function captureNoAccessRedirect(params: {
  authenticated: boolean;
  activeRole: string | null;
  route: string;
}) {
  try {
    const last = Number(sessionStorage.getItem(NO_ACCESS_DEDUP_KEY) ?? 0);
    const now = Date.now();
    if (now - last < NO_ACCESS_DEDUP_WINDOW_MS) return;
    sessionStorage.setItem(NO_ACCESS_DEDUP_KEY, String(now));
  } catch {
    // sessionStorage unavailable — fall through and report.
  }

  Sentry.captureMessage('[auth] landed on /no-access', {
    level: 'warning',
    tags: {
      type: 'no_access_redirect',
      authenticated: String(params.authenticated),
    },
    extra: {
      active_role: params.activeRole,
      route: params.route,
      release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
    },
  });
}

const ROLELESS_SIGNOUT_DEDUP_KEY = 'dallali-roleless-signout-last';

/**
 * Report the roleless guard firing — the "account is not set up" forced signout
 * in useAuth.fetchUserData when user_roles reads empty after the retry.
 *
 * This path is otherwise SILENT, which is why a stale-bundle hit (old release,
 * pre-retry code) couldn't be told apart from a genuine fix-gap (latest release,
 * empty read persisting past the retry window). The `release` tag is the
 * discriminator: events on the CURRENT release mean a user whose roles read
 * still came back empty after the retry — i.e. the empty isn't merely a sub-second
 * race and the recovery needs to be more robust.
 */
export function captureRolelessSignout(params: { userId?: string; route: string }) {
  // Collapse bursts (e.g. several tabs) into one event.
  try {
    const last = Number(sessionStorage.getItem(ROLELESS_SIGNOUT_DEDUP_KEY) ?? 0);
    const now = Date.now();
    if (now - last < SESSION_LOST_DEDUP_WINDOW_MS) return;
    sessionStorage.setItem(ROLELESS_SIGNOUT_DEDUP_KEY, String(now));
  } catch {
    // sessionStorage unavailable — fall through and report.
  }

  Sentry.captureMessage('[auth] roleless guard — forced signout (account not set up)', {
    level: 'warning',
    tags: {
      type: 'roleless_signout',
      where: 'fetchUserData-roleless-guard',
    },
    extra: {
      user_id: params.userId,
      route: params.route,
      release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
    },
  });
}

/** Dedup key for {@link captureAnonRead} — separate from the session-loss and
 *  roleless-signout keys so a benign anon read never collapses (or is collapsed
 *  by) a genuine signout event firing within the same 5s window. */
const ANON_READ_DEDUP_KEY = 'dallali-anon-read-last';

/**
 * Report that the roleless guard saw an EMPTY user_roles read that was NOT
 * authenticated as the user (anon/stale session) and therefore BAILED WITHOUT
 * signing out — the DALLALI-FRONTEND-BF fix. This is benign and self-healing
 * (the next SIGNED_IN re-runs fetchUserData with the token applied), so it is
 * emitted at `warning` level with its OWN message and dedup key. Deliberately
 * distinct from captureSessionLost ("forced signout") and captureRolelessSignout
 * ("forced signout — account not set up"): NO signout happened here, so a triager
 * must not go hunting for a logout that never occurred.
 */
export function captureAnonRead(params: { userId?: string; route: string }) {
  // Own dedup window/key so a genuine session-loss in the same 5s isn't dropped.
  try {
    const last = Number(sessionStorage.getItem(ANON_READ_DEDUP_KEY) ?? 0);
    const now = Date.now();
    if (now - last < SESSION_LOST_DEDUP_WINDOW_MS) return;
    sessionStorage.setItem(ANON_READ_DEDUP_KEY, String(now));
  } catch {
    // sessionStorage unavailable — fall through and report.
  }

  // 'info', not 'warning': the anon-read is a self-healing condition — no
  // signout occurred. Emitting at 'warning' caused Sentry monitor 3033444 to
  // create GitHub issues for a normal race (#2211). Downgraded to 'info' so
  // the signal stays observable without triggering alert rules.
  Sentry.captureMessage('[auth] roleless guard — anon read, no signout', {
    level: 'info',
    tags: {
      type: 'auth_anon_read',
      where: 'fetchUserData-roleless-guard',
    },
    extra: {
      user_id: params.userId,
      route: params.route,
      release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
    },
  });
}

/**
 * Set up global error handlers for errors Sentry doesn't catch by default:
 * - Resource loading failures (images, scripts, CSS)
 * Call after initializeSentry() in main.tsx.
 */
export function setupGlobalErrorHandlers() {
  // Capture resource loading errors (img/script/link 404s).
  // These fire on the element, not on window, so we need capture phase.
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target as unknown) !== (window as unknown) &&
        target instanceof HTMLElement &&
        (target.tagName === 'IMG' ||
          target.tagName === 'SCRIPT' ||
          target.tagName === 'LINK')
      ) {
        const src =
          (target as HTMLImageElement).src ||
          (target as HTMLScriptElement).src ||
          (target as HTMLLinkElement).href ||
          'unknown';
        Sentry.captureMessage(`Resource load failed: ${target.tagName} ${src}`, {
          level: 'warning',
          tags: { type: 'resource_error', tagName: target.tagName },
          extra: { src },
        });
      }
    },
    true, // capture phase — required for resource errors
  );
}

/**
 * Serialize an error object for proper Sentry logging
 * Extracts message, code, details, and hint from Supabase errors
 *
 * @param error - The error object to serialize
 * @returns A string representation of the error
 *
 * @example
 * console.error("Error fetching contracts:", serializeError(error));
 */
export function serializeError(error: unknown): string {
  if (!error) return 'Unknown error';

  // Handle Supabase error structure
  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    const parts: string[] = [];

    // Use presence check (not truthiness) so empty-string messages are still
    // surfaced — a Supabase error with {message: ""} was serialising to the
    // opaque '{"message":""}' string (Sentry issue #2271).
    if (err.message !== undefined && err.message !== null) parts.push(`message: ${err.message || "(empty)"}`);
    if (err.code) parts.push(`code: ${err.code}`);
    if (err.details) parts.push(`details: ${err.details}`);
    if (err.hint) parts.push(`hint: ${err.hint}`);

    if (parts.length > 0) {
      return parts.join(', ');
    }

    // Fallback to JSON.stringify for other objects
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  return String(error);
}

/**
 * Normalize a caught value into a real Error for Sentry.captureException.
 *
 * A plain-object throw (`throw { message }` — the shape of the many
 * `if (error) throw error` Supabase/RPC sites) reaches Sentry as the
 * stackless "Object captured as exception with keys: …" signature (#3771),
 * hiding the true origin. Wrapping in an Error restores a readable message
 * and stable message-based grouping; real Errors pass through untouched.
 * The wrapper stack is intentionally dropped (see below) so distinct
 * non-Error crashes don't collapse into one Sentry group under this frame.
 */
export function normalizeCaughtError(caught: unknown): Error {
  if (caught instanceof Error) return caught;
  const normalized = new Error(serializeError(caught));
  // The wrapper's own stack points HERE for every non-Error throw, which
  // would collapse distinct crashes into one Sentry group. Drop it so
  // grouping falls back to the (distinct) message; the actionable context
  // for these events is componentStack + extra.originalError.
  normalized.stack = undefined;
  return normalized;
}
